import { Control, FieldErrors, useController, UseFormSetValue } from 'react-hook-form'
import React, { useEffect, useState } from 'react'
import dayjs from '../../../../dayjsConfig'
import { getTimePeriodNextMonth, getTimePeriodThisMonth } from '../../../../common/helpers/date'
import { List, ListItem, ListItemIcon, ListItemText } from '@material-ui/core'
import { msgid, ngettext, t } from 'ttag'
import { calculateDuration, FormData } from '../../ChallengeAdmin'
import Typography from '@material-ui/core/Typography'

import { ReactComponent as DateNextMonthActiveIcon } from '../../../../assets/v3/icons/date-next-month-active.svg'
import { ReactComponent as DateNextMonthInactiveIcon } from '../../../../assets/v3/icons/date-next-month-inactive.svg'
import { ReactComponent as DateThisMonthActiveIcon } from '../../../../assets/v3/icons/date-this-month-active.svg'
import { ReactComponent as DateThisMonthInactiveIcon } from '../../../../assets/v3/icons/date-this-month-inactive.svg'

import { ReactComponent as DateActiveIcon } from '../../../../assets/v3/icons/date-active.svg'
import { ReactComponent as DateInactiveIcon } from '../../../../assets/v3/icons/date-inactive.svg'
import YumField from '../../../../components/YumInput/YumInput'
import YumMediumTitle from '../../../../components/YumMediumTitle/YumMediumTitle'
import ErrorMessage from '../../../../components/ErrorMessage/ErrorMessage'

interface DateRangeInputProps {
  control: Control<FormData>
  rules: any
  setValue: UseFormSetValue<FormData>
  errors: FieldErrors<FormData>
  isUserCreatedChallenge: boolean
  salespersonLink: any
  contractStartDate: Date | null
  contractEndDate: Date | null
  contractPeriod: any
  plan: string
}

export default function DateRangeInput({
  control,
  rules,
  setValue,
  errors,
  isUserCreatedChallenge,
  salespersonLink,
  contractStartDate,
  contractEndDate,
  contractPeriod,
  plan,
}: DateRangeInputProps) {
  const { field } = useController({ control, name: 'dateRange', rules })

  const [durationDays, setDurationDays] = useState(calculateDuration(field.value[0], field.value[1]))

  const { startDate: startDateThisMonth, endDate: endDateThisMonth } = getTimePeriodThisMonth()
  const { startDate: startDateNextMonth, endDate: endDateNextMonth } = getTimePeriodNextMonth()

  useEffect(() => {
    setValue('days', durationDays)
  }, [durationDays])

  let nextMonthSelected = false
  let thisMonthSelected = false
  let contractPeriodSelected = false
  if (field.value[0] && field.value[1]) {
    nextMonthSelected =
      dayjs(startDateNextMonth).isSame(field.value[0], 'day') && dayjs(endDateNextMonth).isSame(field.value[1], 'day')
    thisMonthSelected =
      dayjs(startDateThisMonth).isSame(field.value[0], 'day') && dayjs(endDateThisMonth).isSame(field.value[1], 'day')
    contractPeriodSelected =
      contractStartDate && contractEndDate
        ? dayjs(contractStartDate).isSame(field.value[0], 'day') && dayjs(contractEndDate).isSame(field.value[1], 'day')
        : false
  }

  const setContractPeriodDates = () => {
    if (contractStartDate && contractEndDate) {
      const startDate = contractStartDate
      const endDate = dayjs(contractEndDate).endOf('day').toDate()
      field.onChange([startDate, endDate])
      const days = calculateDuration(startDate, endDate)
      setDurationDays(days)
    }
  }

  // Logic if we have contract period.
  let showContractPeriodOption = false
  if (contractStartDate && contractEndDate) {
    const contractDays = calculateDuration(contractStartDate, contractEndDate)
    const contractMoreThanMonth = contractDays > 31
    const contractEnded = contractEndDate < new Date()

    // Don't show "This month" and "Last month" options if contract period is short and has not ended.
    if (!contractMoreThanMonth && !contractEnded) {
      showContractPeriodOption = true
    }
  }

  const setThisMonthDates = () => {
    const startDate = new Date(startDateThisMonth)
    const endDate = new Date(endDateThisMonth)

    field.onChange([startDate, endDate])
    const days = calculateDuration(startDate, endDate)
    setDurationDays(days)
  }

  const setNextMonthDates = () => {
    const startDate = new Date(startDateNextMonth)
    const endDate = new Date(endDateNextMonth)

    field.onChange([startDate, endDate])
    const days = calculateDuration(startDate, endDate)
    setDurationDays(days)
  }

  const prepareStartDate = (newStartDate: Date) => {
    return dayjs(newStartDate).startOf('day').toDate()
  }

  const handleStartDateChange = (event: { target: { value: string } }) => {
    const value = event.target.value
    const endDate = field.value[1]

    let newStartDate = startDateNextMonth
    if (value) {
      newStartDate = dayjs(value, 'YYYY-MM-DD').toDate()
      newStartDate = prepareStartDate(newStartDate)
    }

    field.onChange([newStartDate, endDate])

    const days = calculateDuration(newStartDate, endDate)
    setDurationDays(days)
  }

  const prepareEndDate = (newEndDate: Date) => {
    return dayjs(newEndDate).endOf('day').toDate()
  }

  const handleEndDateChange = (event: { target: { value: string } }) => {
    const value = event.target.value
    const startDate = field.value[0]

    let newEndDate = endDateNextMonth
    if (value) {
      newEndDate = dayjs(value, 'YYYY-MM-DD').toDate()
      newEndDate = prepareEndDate(newEndDate)
    }

    field.onChange([startDate, newEndDate])

    const days = calculateDuration(startDate, newEndDate)
    setDurationDays(days)
  }

  return (
    <>
      <YumMediumTitle>{t`Quick selection`}</YumMediumTitle>
      <List component="div" role="radiogroup" aria-label={t`Quick selection`} style={{ marginBottom: '24px' }}>
        {showContractPeriodOption ? (
          <ListItem
            button
            role="radio"
            aria-checked={contractPeriodSelected}
            disableRipple
            onClick={setContractPeriodDates}
            selected={contractPeriodSelected}
          >
            <ListItemIcon>
              {contractPeriodSelected ? <DateThisMonthActiveIcon /> : <DateThisMonthInactiveIcon />}
            </ListItemIcon>
            <ListItemText id="contractPeriod" primary={plan === 'trial' ? t`Trial period` : t`Contract period`} />
          </ListItem>
        ) : (
          <>
            <ListItem
              role="radio"
              aria-checked={nextMonthSelected}
              button
              disableRipple
              onClick={setNextMonthDates}
              selected={nextMonthSelected}
            >
              <ListItemIcon>
                {nextMonthSelected ? <DateNextMonthActiveIcon /> : <DateNextMonthInactiveIcon />}
              </ListItemIcon>
              <ListItemText id="nextMonth" primary={t`Next month`} />
            </ListItem>
            <ListItem
              role="radio"
              aria-checked={thisMonthSelected}
              button
              disableRipple
              onClick={setThisMonthDates}
              selected={thisMonthSelected}
            >
              <ListItemIcon>
                {thisMonthSelected ? <DateThisMonthActiveIcon /> : <DateThisMonthInactiveIcon />}
              </ListItemIcon>
              <ListItemText id="thisMonth" primary={t`This month`} />
            </ListItem>
          </>
        )}
      </List>
      {/* <Typography variant="subtitle2" style={{ marginBottom: 16 }}>
        {t`Custom period`}
      </Typography> */}
      <YumMediumTitle>{t`Custom period`}</YumMediumTitle>
      <div className="date-range" style={{ display: 'flex', gap: 15 }}>
        <YumField
          type="date"
          label={t`Start date`}
          startIcon={DateInactiveIcon}
          /*startIcon={DateActiveIcon}
          startIconInactive={DateInactiveIcon}*/
          // Hand the RHF ref to the input so shouldFocusError can focus it.
          inputRef={field.ref}
          value={field.value[0] && dayjs(field.value[0]).format('YYYY-MM-DD')}
          onChange={handleStartDateChange}
          //error={!field.value[0] || (!!errors.dateRange && errors.dateRange.type === 'startDate')}
          error={!!errors.dateRange}
        />
        <YumField
          type="date"
          label={t`End date`}
          variant="filled"
          startIcon={DateInactiveIcon}
          /*startIcon={DateActiveIcon}
          startIconInactive={DateInactiveIcon}*/
          value={field.value[1] && dayjs(field.value[1]).format('YYYY-MM-DD')}
          onChange={handleEndDateChange}
          //error={!!errors.dateRange && errors.dateRange.type !== 'startDate'}
          error={!!errors.dateRange}
        />
      </div>
      {errors.dateRange ? (
        <>
          <div style={{ textAlign: 'center', marginTop: '-8px' }}>
            <ErrorMessage>{errors.dateRange?.message}</ErrorMessage>
          </div>
          {['startDate', 'endDate'].includes(errors.dateRange.type as string) && (
            <div style={{ textAlign: 'center', lineHeight: '1.5', marginTop: '16px' }}>
              {contractPeriod && <div style={{ marginBottom: '10px' }}>{contractPeriod}</div>}
              {isUserCreatedChallenge ? (
                <div>{t`Please inform your admin that you are interested in extending YuMuuv’s usage period.`}</div>
              ) : (
                <>
                  <div>{t`To extend your platform access, please reach out to your dedicated sales manager at YuMuuv.`}</div>
                  {salespersonLink}
                </>
              )}
            </div>
          )}
        </>
      ) : (
        <>
          {durationDays > 0 && (
            <div style={{ marginTop: '16px', textAlign: 'center' }}>
              {t`Your challenge will last`}:{' '}
              <strong>
                {durationDays} {ngettext(msgid`day`, `days`, durationDays)}
              </strong>
            </div>
          )}
        </>
      )}
      {/* <div style={{ marginTop: 20, textAlign: 'center' }}>
        {t`Users have 24 hours to sync data after the challenge ends.`}
      </div> */}
    </>
  )
}
